import { USDC_NEW_DENOM } from '@/constants/constants'
import {
  TableContainer,
  Table,
  Tbody,
  Tr,
  Td,
  Flex,
  Text,
  Box,
  Thead,
  SkeletonCircle,
  Skeleton,
  TableCellProps,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { GameParticipants } from '@/components/molecules/GameParticipants/GameParticipants'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { Eye } from '@/icons'
import { Image } from '@/components/HOC/Image'
import LootboxIcon from '@/public/assets/images/games-icons/lootbox-icon-96x96.png'
import CoinflipIcon from '@/public/assets/images/games-icons/coinflip-icon-96x96.png'
import RunesIcon from '@/public/assets/images/games-icons/runes-icon-96x96.png'
import PlinkoIcon from '@/public/assets/images/games-icons/plinko-icon-96x96.png'
import JackpotIcon from '@/public/assets/images/games-icons/jackpot-icon-96x96.png'
import DoubleIcon from '@/public/assets/images/double/double-icon.png'
import MinesIcon from '@/public/assets/images/games-icons/mines-icon-96x96.png'
import { UserProfile } from '@/components/atoms/UserProfile/UserProfile'
import { DoubleField } from '@/components/templates/Double/atoms/DoubleField'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import DoubleJackpotIcon from '@/public/assets/images/double/double-jackpot-icon.png'
import Link from 'next/link'

type GameJackpot = {
  id: string
  game: GAME.JACKPOT
  pool: string
  player_winner: string
  players: { address: string; amount: string }[]
  date?: number
}

export type GameDouble = {
  id: string
  gameId: string
  denom: string
  address: string | undefined
  game: GAME.ROULETTE
  bets: Record<DoubleColor, string>
  winColor: DoubleColor
  totalBet: string
  winAmount: string
  jackpotAmount: string
}

export type GameMines = {
  id: string
  game: GAME.MINES
  bet: string
  denom: string
  payable: string
  status: 'PENDING' | 'WIN' | 'LOSE'
  mines: number
  maxCells: number
  tryGemsOpened: number
  gemsOpened: number
  theoreticalGemsOpened: number
}

type GameRecord = GameJackpot | GameDouble | GameMines

export enum GAME {
  JACKPOT = 'JACKPOT',
  ROULETTE = 'ROULETTE',
  MINES = 'MINES',
}

export const TableWrapper = ({
  children,
  headerRow,
}: {
  children: React.ReactElement | React.ReactElement[]
  headerRow?: React.ReactElement
}) => {
  return (
    <TableContainer py={3} px={6} bg={'ninja.600'} borderRadius={'xl'}>
      <Table>
        <Thead>{headerRow}</Thead>
        <Tbody>{children}</Tbody>
      </Table>
    </TableContainer>
  )
}

type TableRecord<T> = {
  id: number
} & T

export function StyledTable2<T>({
  records,
  row,
}: {
  records: TableRecord<T>[]
  row: (record: T, isLastRow: boolean) => React.ReactElement
}) {
  return (
    <TableWrapper>
      {records.map((record, index) => {
        const isLastRow = index === records.length - 1
        return <Tr key={record.id}>{row(record, isLastRow)}</Tr>
      })}
    </TableWrapper>
  )
}

export const StyledTable = ({ games }: { games: Array<GameRecord> }) => {
  return (
    <TableWrapper>
      {games.map((game, index) => {
        const isLastRow = index === games.length - 1
        return (
          <Tr key={game.id}>
            {game.game === GAME.JACKPOT && <JackpotRow isLastRow={isLastRow} game={game} />}
            {game.game === GAME.ROULETTE && <DoubleRow isLastRow={isLastRow} game={game} />}
            {game.game === GAME.MINES && <MinesRow isLastRow={isLastRow} game={game} />}
          </Tr>
        )
      })}
    </TableWrapper>
  )
}

export const SkeletonStyledTable = ({
  numberOfRows = 5,
  withHeader,
}: {
  numberOfRows?: number
  withHeader?: boolean
}) => {
  return (
    <TableWrapper headerRow={withHeader ? <SkeletonHeader /> : undefined}>
      {Array.from({ length: numberOfRows }, (_v, k) => {
        return (
          <Tr key={k}>
            <SkeletonRow isLastRow={k === numberOfRows - 1} />
          </Tr>
        )
      })}
    </TableWrapper>
  )
}

const SkeletonHeader = () => {
  return <></>
}

const SkeletonRow = ({ isLastRow }: { isLastRow: boolean }) => {
  return (
    <>
      <Td pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={3} alignItems={'center'} width={'350px'}>
          <SkeletonCircle startColor="ninja.200" endColor="ninja.300" size="10" />
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="20px" width={'100px'} />
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={3} alignItems={'center'}>
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="20px" width={'40px'} />
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="40px" width={'50px'} />
        </Flex>
      </Td>
      <Td border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'} px={2} w={200} minW={160}>
        <Flex gap={3} alignItems={'center'}>
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="20px" width={'140px'} />
        </Flex>
      </Td>
      <Td px={2} w={200} minW={200} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={3} alignItems={'center'} justifyContent={'end'}>
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="20px" width={'150px'} />
        </Flex>
      </Td>
      <Td pr={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={3} alignItems={'center'}>
          <Skeleton startColor="ninja.200" endColor="ninja.300" height="20px" width={'20px'} />
        </Flex>
      </Td>
    </>
  )
}

type RowValues = {
  winner: React.ReactElement
  opponents: React.ReactElement
  gameIcon: React.ReactElement
  bet: string
  reward: string
  callback: () => void
}

export const Participants = ({ winner, opponents }: { winner: string; opponents: Array<string> }) => {
  const { t } = useTranslation()

  return (
    <Flex gap={3} alignItems={'center'}>
      <UserProfile address={winner} />
      <Box fontSize={'xs'} fontWeight={600} color={'ninja.100'} py={1} px={2} bg={'ninja.500'} rounded={'md'}>
        {t('common.vs')}
      </Box>
      <GameParticipants players={opponents} winner={winner} />
    </Flex>
  )
}

const TableRow = ({ values, isLastRow }: { values: RowValues; isLastRow: boolean }) => {
  const { t } = useTranslation()

  return (
    <>
      <Td pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={3} alignItems={'center'}>
          {values.winner}
          <Box fontSize={'xs'} fontWeight={600} color={'ninja.100'} py={1} px={2} bg={'ninja.500'} rounded={'md'}>
            {t('common.vs')}
          </Box>
          {values.opponents}
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.in-game')}
          </Text>
          <Box position={'relative'}>
            <Box
              transform={'skew(-15deg)'}
              w={'48px'}
              h={42}
              pt={'1px'}
              pl={'1px'}
              ml={1.5}
              bgGradient={`linear(to-b, #403F8D, #403F8D)`}
              rounded={'md'}
            >
              <Box w={46} h={10} bgGradient={`linear(to-b, #1D1C49, #323179)`} rounded={'md'} />
            </Box>
            {values.gameIcon}
          </Box>
        </Flex>
      </Td>
      <Td px={2} w={200} minW={160} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.bet')}
          </Text>
          <Flex alignItems={'center'} gap={1.5}>
            <FiatOrCryptoAmount
              iconSize={5}
              value={new TokenMoney(values.bet, USDC_NEW_DENOM)}
              wrapperStyles={{ fontSize: 'sm', fontWeight: 700, color: 'ninja.100' }}
            />
          </Flex>
        </Flex>
      </Td>
      <Td px={2} w={200} minW={200} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.won')}
          </Text>
          <Flex alignItems={'center'} gap={1.5}>
            <FiatOrCryptoAmount
              iconSize={5}
              value={new TokenMoney(values.reward, USDC_NEW_DENOM).humanFormat}
              denom={USDC_NEW_DENOM}
              wrapperStyles={{ fontSize: 'sm', fontWeight: 700, color: 'ninja.100' }}
            />
          </Flex>
        </Flex>
      </Td>
      <Td
        border={isLastRow ? 'none' : 'px'}
        borderColor={'ninja.500'}
        onClick={values.callback}
        cursor={'pointer'}
        pr={0}
        _hover={{
          opacity: 0.75,
        }}
      >
        <Eye />
      </Td>
    </>
  )
}

export const AmountCell = ({
  isLastRow,
  styles,
  value,
  text,
}: {
  isLastRow: boolean
  styles?: TableCellProps
  value: string
  text: string
}) => {
  return (
    <Td px={2} w={200} minW={160} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'} {...styles}>
      <Flex alignItems={'center'} gap={3}>
        <Text color={'ninja.200'} fontSize={'xs'}>
          {text}
        </Text>
        <FiatOrCryptoAmount
          value={new TokenMoney(value, USDC_NEW_DENOM)}
          iconSize={5}
          wrapperStyles={{
            fontSize: 'sm',
            color: 'ninja.100',
            fontWeight: 700,
          }}
          currencyStyles={{
            color: 'ninja.300',
            fontWeight: 500,
          }}
        />
      </Flex>
    </Td>
  )
}

import { BigNumberInBase } from '@injectivelabs/utils'
import { colors } from '@/theme'
import { StepBadge, StepBadgeColor } from '@/components/atoms/StepBadge/StepBadge'
import { MinePanel } from '@/components/organisms/MinesGamePlayground/MinesGamePlayground'
import { getTokenByCurrency } from '@/services/token-service'
import { DoubleColor } from '@/__generated__/graphql'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { Game } from 'types/types'
import { ReactNode } from 'react'

const DoubleRow = ({ game, isLastRow }: { game: GameDouble; isLastRow: boolean }) => {
  const modal = useAppModal()
  const { t } = useTranslation()

  const handleOpenModal = () => {
    modal.openModalNew(AppModals.DoubleGameDetails, {
      gameId: game.gameId.toString(),
      address: game.address!,
      denom: game.denom,
    })
  }

  const profit = new BigNumberInBase(game.winAmount).minus(game.totalBet).plus(game.jackpotAmount)

  return (
    <>
      <Td minW={'510px'} pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex gap={6}>
          <Flex gap={3} flexShrink={0}>
            <Box flexShrink={0}>
              <DoubleField isDisabled={game.winColor !== DoubleColor.Black} type={DoubleColor.Black} boxSize={10} />
            </Box>
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              denom={game.denom}
              value={new TokenMoney(game.bets[DoubleColor.Black], game.denom).humanFormat}
            />
          </Flex>
          <Flex gap={3} flexShrink={0}>
            <Box flexShrink={0}>
              <DoubleField isDisabled={game.winColor !== DoubleColor.Green} type={DoubleColor.Green} boxSize={10} />
            </Box>
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              denom={game.denom}
              value={new TokenMoney(game.bets[DoubleColor.Green], game.denom).humanFormat}
            />
          </Flex>
          <Flex gap={3} flexShrink={0}>
            <Box flexShrink={0}>
              <DoubleField isDisabled={game.winColor !== DoubleColor.Red} type={DoubleColor.Red} boxSize={10} />
            </Box>
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'sm',
              }}
              iconSize={'18px'}
              denom={game.denom}
              value={new TokenMoney(game.bets[DoubleColor.Red], game.denom).humanFormat}
            />
          </Flex>
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.in-game')}
          </Text>
          <Box position={'relative'}>
            <Box
              transform={'skew(-15deg)'}
              w={'48px'}
              h={42}
              pt={'1px'}
              pl={'1px'}
              ml={1.5}
              bgGradient={`linear(to-b, #403F8D, #403F8D)`}
              rounded={'md'}
            >
              <Box w={46} h={10} bgGradient={`linear(to-b, #1D1C49, #323179)`} rounded={'md'} />
            </Box>
            <Image maxW={'none'} position={'absolute'} left={1} top={-1.5} src={DoubleIcon} alt={''} />
          </Box>
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        {game.jackpotAmount !== '0' && (
          <Flex justifyContent={'end'}>
            <Flex
              w={'max-content'}
              pb={1}
              pt={1.5}
              pr={3}
              rounded={'lg'}
              alignItems={'center'}
              justifyContent={'end'}
              border={'1px solid'}
              borderColor={'ninja.400'}
              bg={'whiteAlpha.100'}
              h={8}
              bgGradient={colors.gradient.primary}
            >
              <Image pos={'relative'} h={16} marginLeft={-8} w={'auto'} top={-1} src={DoubleJackpotIcon} alt={''} />
              <Box lineHeight={1}>
                <FiatOrCryptoAmount
                  denom={USDC_NEW_DENOM}
                  wrapperStyles={{
                    fontSize: 'sm',
                  }}
                  iconSize={'18px'}
                  currencyStyles={{
                    color: 'ninja.100',
                  }}
                  value={
                    new TokenMoney(game.jackpotAmount, getTokenByCurrency(SupportedCurrencies.USDC).denom).humanFormat
                  }
                />
              </Box>
            </Flex>
          </Flex>
        )}
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <FiatOrCryptoAmount
            wrapperStyles={{
              fontSize: 'sm',
              color: profit.isNegative() ? 'brand.error' : profit.isZero() ? 'brand.white' : '#66c16f',
            }}
            iconSize={'18px'}
            currencyStyles={{
              color: 'ninja.300',
            }}
            value={new TokenMoney(profit.toString(), game.denom).humanFormat}
            denom={game.denom}
          />
        </Flex>
      </Td>
      <Td
        border={isLastRow ? 'none' : 'px'}
        borderColor={'ninja.500'}
        onClick={handleOpenModal}
        cursor={'pointer'}
        pr={0}
        _hover={{
          opacity: 0.75,
        }}
      >
        <Eye />
      </Td>
    </>
  )
}

export const MinesGameElement = ({
  status,
  mines,
  gemsOpened,
}: {
  status: string
  mines: number
  gemsOpened: number
}) => {
  return (
    <Flex gap={3}>
      <Box flexShrink={0} w={'50px'} pos={'relative'} border={'1px solid'} borderColor={'ninja.500'} rounded={'xl'}>
        <MinePanel
          isMine={true}
          isActive={status === 'LOSE'}
          styles={{
            boxShadow: '0px -3px 15px 0px rgba(231, 31, 31, 0.24) inset;',
          }}
        />
        <Text
          fontWeight={500}
          zIndex={10}
          lineHeight={1}
          pos={'absolute'}
          fontSize={'xs'}
          color={'whiteAlpha.700'}
          left={1}
          bottom={1}
        >
          {mines}
        </Text>
      </Box>
      <Flex
        flexShrink={0}
        pos={'relative'}
        alignItems={'center'}
        border={'1px solid'}
        borderColor={'ninja.500'}
        rounded={'xl'}
      >
        <Box w={12} flexShrink={0}>
          <MinePanel
            isMine={false}
            isActive={true}
            styles={{
              boxShadow: '0px -3px 15px 0px rgba(104, 242, 118, 1) inset;',
            }}
          />
        </Box>
        <Text px={4} flexShrink={0} fontWeight={700}>
          {gemsOpened}
        </Text>
        <Text
          fontWeight={500}
          zIndex={10}
          lineHeight={1}
          pos={'absolute'}
          fontSize={'xs'}
          color={'whiteAlpha.700'}
          left={1}
          bottom={1}
        >
          {25 - mines}
        </Text>
      </Flex>
    </Flex>
  )
}

const MinesRow = ({ game, isLastRow }: { game: GameMines; isLastRow: boolean }) => {
  const modal = useAppModal()
  const { t } = useTranslation()

  const handleOpenModal = () => {
    modal.openModalNew(AppModals.MinesGameDetails, { gameId: game.id })
  }

  return (
    <>
      <Td pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <MinesGameElement status={game.status} mines={game.mines} gemsOpened={game.gemsOpened} />
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.in-game')}
          </Text>
          <Box position={'relative'}>
            <Box
              transform={'skew(-15deg)'}
              w={'48px'}
              h={42}
              pt={'1px'}
              pl={'1px'}
              ml={1.5}
              bgGradient={`linear(to-b, #403F8D, #403F8D)`}
              rounded={'md'}
            >
              <Box w={46} h={10} bgGradient={`linear(to-b, #1D1C49, #323179)`} rounded={'md'} />
            </Box>
            <Image maxW={'none'} position={'absolute'} left={2.5} top={-1} w={10} src={MinesIcon} alt={''} />
          </Box>
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.bet')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            denom={game.denom}
            value={new TokenMoney(game.bet, game.denom || 'inj').humanFormat}
          />
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'center'} gap={3}>
          <StepBadge
            content={'x' + new BigNumberInBase(game.payable).dividedBy(game.bet).toFixed(2)}
            color={game.status === 'LOSE' ? StepBadgeColor.RED : StepBadgeColor.GREEN}
          />
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.reward')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              color: Number(game.payable) == 0 ? 'brand.white' : '#66c16f',
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            denom={game.denom}
            value={new TokenMoney(game.payable, game.denom || 'inj').humanFormat}
          />
        </Flex>
      </Td>
      <Td
        border={isLastRow ? 'none' : 'px'}
        borderColor={'ninja.500'}
        onClick={handleOpenModal}
        cursor={'pointer'}
        pr={0}
        _hover={{
          opacity: 0.75,
        }}
      >
        <Flex justifyContent={'end'}>
          <Eye />
        </Flex>
      </Td>
    </>
  )
}

export type UserBet = {
  game: Game
  widget: ReactNode
  bet: {
    amount: string
    denom: string
  }
  multiplier: {
    number: string
    isWon: boolean
  }
  reward: {
    amount: string
    denom: string
  }
  gameId: string
  betId: string
  createdAt?: string
}

export const GenericRow = ({
  game,
  widget,
  bet,
  multiplier,
  reward,
  gameId,
  isLastRow,
}: UserBet & { isLastRow: boolean }) => {
  const modal = useAppModal()
  const { t } = useTranslation()

  const handleOpenModal = () => {
    if (game === Game.COINFLIP) {
      modal.openModalNew(AppModals.CoinflipGameDetails, { gameId })
    }

    if (game === Game.MINES) {
      modal.openModalNew(AppModals.MinesGameDetails, { gameId })
    }

    if (game === Game.JACKPOT) {
      modal.openModalNew(AppModals.GameStats, { gameId })
    }

    if (game === Game.RUNES) {
      modal.openModalNew(AppModals.RunesGameDetails, { gameId })
    }

    if (game === Game.PLINKO) {
      modal.openModalNew(AppModals.PlinkoGameDetails, { gameId })
    }
  }

  return (
    <>
      <Td pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        {widget}
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.in-game')}
          </Text>
          <Box position={'relative'}>
            <Box
              transform={'skew(-15deg)'}
              w={'48px'}
              h={42}
              pt={'1px'}
              pl={'1px'}
              ml={1.5}
              bgGradient={`linear(to-b, #403F8D, #403F8D)`}
              rounded={'md'}
            >
              <Box w={46} h={10} bgGradient={`linear(to-b, #1D1C49, #323179)`} rounded={'md'} />
            </Box>
            {game === Game.MINES && (
              <Image maxW={'none'} position={'absolute'} left={1.5} top={-2} w={12} src={MinesIcon} alt={''} />
            )}
            {(game as any) === 'lootbox-unlimited' && (
              <Image maxW={'none'} position={'absolute'} left={1.5} top={-2} w={12} src={LootboxIcon} alt={''} />
            )}{' '}
            {game === Game.JACKPOT && (
              <Image maxW={'none'} position={'absolute'} left={2.5} top={-1.5} w={10} src={JackpotIcon} alt={''} />
            )}
            {game === Game.COINFLIP && (
              <Image maxW={'none'} position={'absolute'} left={1.5} top={-2} w={12} src={CoinflipIcon} alt={''} />
            )}
            {game === Game.RUNES && (
              <Image maxW={'none'} position={'absolute'} left={2} top={-2} w={12} src={RunesIcon} alt={''} />
            )}
            {game === Game.PLINKO && (
              <Image maxW={'none'} position={'absolute'} left={2.5} top={-1.5} w={10} src={PlinkoIcon} alt={''} />
            )}
            {game === Game.GAMEZONE && (
              <Image maxW={'none'} position={'absolute'} left={2.5} top={-1.5} w={10} src={PlinkoIcon} alt={''} />
            )}
          </Box>
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.bet')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            value={new TokenMoney(bet.amount, bet.denom)}
          />
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'center'} gap={3}>
          <StepBadge
            content={'x' + multiplier.number}
            color={!multiplier.isWon ? StepBadgeColor.RED : StepBadgeColor.GREEN}
          />
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.reward')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              color: !multiplier.isWon ? 'ninja.200' : '#66c16f',
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            value={new TokenMoney(reward.amount, reward.denom)}
          />
        </Flex>
      </Td>
      {(game as any) !== 'lootbox-unlimited' && (
        <Td
          border={isLastRow ? 'none' : 'px'}
          borderColor={'ninja.500'}
          onClick={handleOpenModal}
          cursor={'pointer'}
          pr={0}
          _hover={{
            opacity: 0.75,
          }}
        >
          <Flex justifyContent={'end'}>
            <Eye />
          </Flex>
        </Td>
      )}
    </>
  )
}

export const GameZoneRow = ({
  bet,
  multiplier,
  reward,
  gameId,
  createdAt,
  isLastRow,
  extra,
}: UserBet & { isLastRow: boolean } & { extra?: { game_id: string } }) => {
  const { t } = useTranslation()
  const betDate = createdAt ? new Date(createdAt) : null

  return (
    <>
      <Td pl={0} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Text fontSize={'sm'} fontWeight={700} color={'ninja.200'}>
          #{gameId}
        </Text>
        {betDate && !Number.isNaN(betDate.getTime()) && (
          <Text
            as={'time'}
            dateTime={betDate.toISOString()}
            display={'block'}
            mt={1}
            fontSize={'xs'}
            color={'ninja.200'}
          >
            {betDate.toLocaleString()}
          </Text>
        )}
      </Td>
      <Td px={2} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'flex-start'} gap={3}>
          <Box
            fontWeight={700}
            color={'ninja.100'}
            _hover={{
              color: 'ninja.200',
            }}
            transition={'0.1s'}
          >
            <Link href={'/game-zone/' + extra?.game_id}>
              <Text as={'span'} fontSize={'sm'} textTransform={'capitalize'}>
                {extra?.game_id.replace(/-/g, ' ')}
              </Text>
            </Link>
          </Box>
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.bet')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            value={new TokenMoney(bet.amount, bet.denom)}
          />
        </Flex>
      </Td>
      <Td px={2} w={180} minW={180} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'center'} gap={3}>
          <StepBadge
            content={'x' + multiplier.number}
            color={!multiplier.isWon ? StepBadgeColor.RED : StepBadgeColor.GREEN}
          />
        </Flex>
      </Td>
      <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
        <Flex alignItems={'center'} justifyContent={'end'} gap={3}>
          <Text color={'ninja.200'} fontSize={'xs'}>
            {t('common.reward')}
          </Text>
          <FiatOrCryptoAmount
            wrapperStyles={{
              color: !multiplier.isWon ? 'ninja.200' : '#66c16f',
              fontSize: 'sm',
            }}
            iconSize={'18px'}
            value={new TokenMoney(reward.amount, reward.denom)}
          />
        </Flex>
      </Td>
    </>
  )
}

export const ViewMore = ({ isLastRow, callback }: { isLastRow: boolean; callback: () => void }) => {
  return (
    <Td
      border={isLastRow ? 'none' : 'px'}
      borderColor={'ninja.500'}
      onClick={callback}
      cursor={'pointer'}
      pr={0}
      _hover={{
        opacity: 0.75,
      }}
    >
      <Eye />
    </Td>
  )
}

export const InGameElement = ({ isLastRow }: { isLastRow: boolean }) => {
  const { t } = useTranslation()

  return (
    <Td px={2} w={160} minW={140} border={isLastRow ? 'none' : 'px'} borderColor={'ninja.500'}>
      <Flex alignItems={'center'} gap={3}>
        <Text color={'ninja.200'} fontSize={'xs'}>
          {t('common.in-game')}
        </Text>
        <Box position={'relative'}>
          <Box
            transform={'skew(-15deg)'}
            w={'48px'}
            h={42}
            pt={'1px'}
            pl={'1px'}
            ml={1.5}
            bgGradient={`linear(to-b, #403F8D, #403F8D)`}
            rounded={'md'}
          >
            <Box w={46} h={10} bgGradient={`linear(to-b, #1D1C49, #323179)`} rounded={'md'} />
          </Box>
          <Image maxW={'none'} position={'absolute'} left={2} top={-3} src={JackpotIcon} alt={''} />
        </Box>
      </Flex>
    </Td>
  )
}

export const JackpotRow = ({ game, isLastRow }: { game: GameJackpot; isLastRow: boolean }) => {
  const modal = useAppModal()

  const handleOpenModal = () => {
    modal.openModalNew(AppModals.GameStats, { gameId: game.id })
  }

  const bet = game.players
    ?.filter((x: any) => x.address === game.player_winner)
    .map((x: any) => x.amount)
    .reduce((x: string, y: string) => parseInt(x) + parseInt(y), 0)

  return (
    <TableRow
      isLastRow={isLastRow}
      values={{
        winner: <UserProfile address={game.player_winner!} />,
        opponents: (
          <GameParticipants players={game.players.map((player) => player.address)} winner={game.player_winner!} />
        ),
        gameIcon: <Image maxW={'none'} position={'absolute'} left={2} top={-3} src={JackpotIcon} alt={''} />,
        bet: bet,
        reward: game.pool,
        callback: handleOpenModal,
      }}
    />
  )
}
